// Centro de ayuda: explica cada modulo con pantallas ilustradas (los datos
// sensibles van tapados con ███). Cada usuario ve SOLO la ayuda de los
// modulos que tiene permitidos.

import { api } from "../api";
import { iconoRuta, iconoAyuda, logoApp } from "../iconos";
import type { Sesion } from "../../lib/types";

interface SeccionAyuda {
  permiso: string | null; // null = todos; '__admin' = solo admin
  hash: string; // para el icono
  titulo: string;
  queEs: string;
  mock: string;
  pasos: string[];
  tips: string[];
  // La seccion no corresponde a ninguna pantalla del menu: no lleva el "Ir a…".
  sinEnlace?: boolean;
}

const T = (txt: string) => `<span class="tapado">${txt}</span>`;

const SECCIONES: SeccionAyuda[] = [
  {
    permiso: "pedido",
    hash: "#/pedido",
    titulo: "Pedido",
    queEs:
      "Es la pantalla principal: se toma el pedido de un cliente para entregar (por defecto) mañana. Al confirmar, el pedido suma a la producción del día y genera la COMPRA en la cuenta corriente del cliente.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2">
          <span class="mock-campo">Cliente: LO0001 — ${T("XXXXXXXXXX")}</span>
          <span class="mock-campo">Empresa: Piedra y Sol</span>
          <span class="mock-campo">Entrega: 17/07/2026</span>
          <span class="mock-btn-sec">Sugerir cantidades</span>
        </div>
        <div class="mock-campo w-full">🔍 Buscar un artículo por nombre o código...</div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">▾ SANDWICHERÍA</div>
          <div class="flex items-center justify-between gap-2">
            <span>Olimpico blanco <span class="text-masa-400">#2005 · x1</span></span>
            <span class="mock-campo">$ ${T("999")}</span>
            <span class="flex items-center gap-1"><span class="mock-btn-sec">−</span><span class="mock-campo">3</span><span class="mock-btn-sec">+</span></span>
            <span class="font-bold">$ ${T("9.999")}</span>
          </div>
          <div class="mt-1 text-center text-masa-400">+ Agregar artículo</div>
        </div>
        <div class="flex items-center justify-between border-t border-crema-200 pt-2">
          <span>TOTAL ESTIMADO <b>$ ${T("9.999")}</b></span>
          <span class="flex gap-1"><span class="mock-btn-sec">Ver solo cargados</span><span class="mock-btn-sec">Limpiar</span><span class="mock-btn">Confirmar pedido</span></span>
        </div>
      </div>`,
    pasos: [
      "El <b>cliente</b> y la <b>empresa emisora</b> vienen puestos si hay una sola opción de cada uno (hoy: Tres Cruces y Confitería Piedra y Sol), y el catálogo carga solo. Si algún día hay más de un local, el selector vuelve a pedir que elijas.",
      "Elegí el <b>turno</b> (mañana o tarde): la cuadra separa la producción del día en esos dos bloques.",
      "Cargá las <b>cantidades</b> con los botones − / + (suman de a 1) o escribiendo el número a mano — <b>acepta fracciones</b> (ej: media bandeja de scones = 0.5). El total se actualiza abajo.",
      "Si sabés qué artículo es pero no en qué categoría cae, escribilo en el <b>buscador</b>: elegís de la lista y la pantalla <b>abre la categoría, marca la fila en amarillo</b> y deja el cursor en la cantidad, listo para escribirla.",
      "Si el cliente pide 'lo de siempre', tocá <b>Sugerir cantidades</b>: se precarga su último pedido y ajustas lo que cambie.",
      "Tocá <b>Confirmar pedido</b> → se guarda, impacta producción y cuenta corriente, y aparece el <b>remito</b>. Al imprimir salen <b>dos vias</b>: Via 1 (cliente) y Via 2 (control).",
    ],
    tips: [
      "El pedido en curso <b>no se pierde</b>: si cambias de pantalla o se recarga el navegador, sigue donde lo dejaste.",
      "<b>Agregar / quitar artículos</b> (al pie de cada categoría) abre la lista completa con casillas: tildas lo que va y destildas lo que no, todo de una vez.",
      "El botón del tachito también <b>saca</b> un artículo directo desde la grilla (no borra nada del catálogo).",
      "El buscador mira <b>todo el catálogo del cliente</b>, no solo lo que está a la vista: si el artículo lo habías sacado del pedido, no tiene precio de lista o estabas viendo solo los cargados, elegirlo lo <b>devuelve a la grilla</b>.",
      "Busca sin tildes y por partes: <b>gales</b> encuentra \"Torta galés\", y <b>choco maicena</b> encuentra \"Alfajor de chocolate y maicena\". Con las flechas ↑ ↓ y <b>Enter</b> se elige sin soltar el teclado.",
      "Solo el <b>administrador</b> puede ajustar el precio de una linea (queda marcado en ambar; la flechita vuelve al precio de lista).",
    ],
  },
  {
    permiso: "pedidosweb",
    hash: "#/pedidos-web",
    titulo: "Pedidos web",
    queEs:
      "La bandeja de los pedidos que mandaron los locales desde el celular. Entran en PENDIENTE: todavia NO se producen ni figuran en la cuenta. Recién cuando los confirmás se congelan los precios, entran a la producción del día y se genera la COMPRA.",
    mock: `
      <div class="mock space-y-2">
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="flex items-center justify-between">
            <span><b>${T("XXXXXXXXXX")}</b> <span class="text-masa-400">LO0001 · pedido #12 · entrega 17/07/2026</span></span>
            <span class="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">Pendiente</span>
          </div>
          <div class="text-masa-500"><b>10</b> Empanada J y Q &nbsp; <b>2</b> Scones (bandeja 16 un.) &nbsp; <b>6</b> Olimpico blanco</div>
          <div class="mt-1 flex items-center justify-between">
            <span class="font-bold">$ ${T("9.999")}</span>
            <span class="flex gap-1"><span class="mock-btn-sec">Rechazar</span><span class="mock-btn">Confirmar</span></span>
          </div>
        </div>
      </div>`,
    pasos: [
      "Cuando un local manda un pedido te llega un <b>aviso</b> (el numerito en la campana de arriba y, si lo habilitaste, la notificación al celular).",
      "<b>No hace falta recargar</b>: la bandeja se actualiza sola cada minuto y también apenas volvés a la app. Cuando entra uno nuevo aparece arriba el cartel <b>\"Llego 1 pedido nuevo\"</b>. Si tenés abierto el cartel de confirmar o rechazar, la lista no se mueve hasta que lo cierres.",
      "Revisá el detalle: debajo del nombre del local están los artículos con sus cantidades.",
      "Tocá <b>Confirmar</b> → los precios se releen de la lista del local y quedan congelados, el pedido entra a <b>Producción</b> del día de entrega, se anota la <b>COMPRA</b> en su cuenta y se abre el <b>remito de 2 vias</b> para imprimir.",
      "Si algo no va, tocá <b>Rechazar</b>: el pedido queda anulado, al local le llega un <b>aviso al celular</b> y lo ve como cancelado. Si el motivo necesita explicación, conviene igual una llamada.",
    ],
    tips: [
      "Mientras el pedido este pendiente, el local lo puede <b>corregir o cancelar solo</b>. Una vez confirmado ya no lo toca: tiene que llamarte, y lo anulas vos desde <b>Pedidos</b>.",
      "El precio que valia es el del <b>momento de confirmar</b>, no el de cuando el local lo mandó. Si hubo una suba en el medio, se cobra el nuevo.",
      "Un pedido pendiente <b>no aparece</b> en Producción: la cuadra nunca ve algo sin confirmar.",
    ],
  },
  {
    permiso: "pedidos",
    hash: "#/pedidos",
    titulo: "Pedidos",
    queEs:
      "La lista de <b>todos</b> los pedidos, en cualquier estado: pendientes, confirmados y anulados. Es la pantalla donde se mira un pedido puntual y donde se <b>anula</b> uno que ya se había confirmado. No confundir con <b>Pedidos web</b>, que muestra solo los pendientes y sirve para confirmarlos.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2">
          <span class="mock-campo">Estado: Todos</span>
          <span class="mock-campo">Entrega desde / hasta</span>
          <span class="mock-btn-sec">Recientes</span>
          <span class="mock-btn-sec">Día</span>
          <span class="mock-btn-sec">Semana</span>
        </div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="flex items-center justify-between">
            <span><b>${T("XXXXXXXXXX")}</b> <span class="text-masa-400">LO0001 · <u>pedido #12</u> · entrega 17/07/2026</span></span>
            <span class="rounded-full bg-green-100 px-2 py-0.5 text-green-800">Confirmado</span>
          </div>
          <div class="mt-1 flex items-center justify-between">
            <span class="font-bold">$ ${T("9.999")}</span>
            <span class="mock-btn">Anular</span>
          </div>
        </div>
      </div>`,
    pasos: [
      "Arranca en <b>Recientes</b>: los últimos pedidos sin importar la fecha, que es lo que necesitás al entrar. Con <b>Día / Semana / Mes</b> o las fechas a mano filtrás por <b>fecha de entrega</b>.",
      "El <b>Estado</b> acota a pendientes, confirmados o anulados. Por defecto muestra todos.",
      "Tocá el <b>número del pedido</b> y se abre un <b>resumen</b>: cada artículo con su cantidad, su precio y su importe, y el total al pie. Es para mirar en pantalla — el <b>remito</b> para imprimir sale de Producción o de Pedidos web.",
      "Para anular hay que <b>habilitarlo primero</b>: el botón <b>Habilitar anulaciones</b>, arriba, se pone rojo y recién entonces aparece el <b>Anular</b> en los pedidos confirmados. Arranca apagado siempre —y se apaga de nuevo cada vez que entrás— para que un roce con el pulgar mientras scrolleás la lista no deshaga un pedido.",
      "El <b>Anular</b> sale solo en los <b>confirmados</b>, y solo si tu usuario tiene el permiso. Un pendiente no se anula acá: se confirma o se rechaza desde <b>Pedidos web</b>.",
    ],
    tips: [
      "Anular deshace el pedido completo: sale de la producción del día, al local le llega un aviso al celular y en la cuenta la compra queda compensada con una <b>ANULACIÓN</b>. Nada se borra.",
      "Son <b>dos permisos distintos</b>: <b>Pedidos (lista)</b> deja ver esta pantalla, y <b>Anular pedidos</b> deja deshacerlos. Se pueden dar por separado — alguien puede mirar los pedidos sin poder anular ninguno.",
      "El mismo <b>Anular</b> está también en <b>Cuentas</b>, al lado de cada compra, para cuando lo que estás mirando es el estado de cuenta de un mes viejo. Ahí hay que habilitarlo primero con un botón.",
    ],
  },
  {
    permiso: "produccion",
    hash: "#/produccion",
    titulo: "Producción",
    queEs:
      "Muestra lo que la cuadra tiene que producir para una fecha. Arriba, UN resumen con todos los pedidos del día separados en mañana y tarde; abajo, la hoja de PRODUCCIÓN MAÑANA y la de PRODUCCIÓN TARDE, cada una con su botón de imprimir.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex gap-2"><span class="mock-campo">Fecha: 17/07/2026</span><span class="mock-btn">Ver</span><span class="mock-btn-sec">Imprimir</span></div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">RESUMEN DE PEDIDOS DEL DÍA</div>
          <div class="flex justify-between"><span>${T("XXXXXXX")} <span class="text-masa-400">#12</span></span><span>$ ${T("9.999")}</span></div>
          <div class="flex justify-between border-t border-crema-100 font-bold"><span>TOTAL DEL DÍA</span><span>$ ${T("99.999")}</span></div>
        </div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 flex justify-between font-bold text-masa-700"><span>PRODUCCIÓN MAÑANA</span><span class="mock-btn">Imprimir</span></div>
          <div class="mb-1 font-bold text-masa-500">PANADERÍA</div>
          <table class="w-full"><tr class="text-masa-400"><td>Artículo</td><td class="text-right">Unidades</td></tr>
          <tr><td>Scones (bandeja 16 un.)</td><td class="text-right font-bold">48</td></tr></table>
        </div>
      </div>`,
    pasos: [
      "Elegí la <b>fecha</b> (por defecto muestra mañana) y tocá <b>Ver</b>.",
      "Arriba de todo, el <b>resumen de pedidos del día</b>: primero los de mañana, después los de tarde, con el subtotal de cada turno y el total del día al pie. Tocá un cliente y se abre su <b>remito</b>.",
      "Abajo van las dos hojas para la cuadra: <b>PRODUCCIÓN MAÑANA</b> y <b>PRODUCCIÓN TARDE</b>, con artículo y unidades por área, sin clientes.",
      "El botón de cada hoja imprime <b>solo ese turno</b>: el de mañana no trae nada de la tarde, ni el resumen. El botón de <b>arriba</b>, en cambio, imprime toda la página.",
    ],
    tips: [
      "Si anulas un pedido, desaparece solo de la producción de esa fecha.",
      "Antes había una tabla por área con una <b>columna por cliente</b>. Se sacó: con un solo local repetía la misma cifra y en el celular obligaba a mover la pantalla de costado. Lo que pidió cada local está en su remito, a un toque desde el resumen.",
    ],
  },
  {
    permiso: "cuentas",
    hash: "#/cuentas",
    titulo: "Cuentas",
    queEs:
      "La cuenta corriente de cada cliente: compras, devoluciones, faltantes y pagos, con el saldo siempre calculado. Desde acá se imprime el estado de cuenta (factura interna).",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">Cliente: ${T("XXXXXXXXXX")}</span><span class="mock-campo">Desde 01/07</span><span class="mock-campo">Hasta 16/07</span><span class="mock-btn-sec">Día</span><span class="mock-btn-sec">Semana</span><span class="mock-btn-sec">Mes</span><span class="mock-btn-sec">Con detalles</span><span class="mock-btn-sec">Imprimir</span></div>
        <table class="w-full"><tr class="text-masa-400"><td>Fecha</td><td>Detalle</td><td>Compra</td><td>Pago</td><td>Saldo</td></tr>
          <tr><td>16/07</td><td>COMPRA <span class="text-masa-400">#12</span></td><td>$ ${T("9.999")}</td><td></td><td>$ ${T("9.999")}</td></tr>
          <tr><td>16/07</td><td>PAGO</td><td></td><td>$ ${T("5.000")}</td><td>$ ${T("4.999")}</td></tr>
        </table>
        <div class="flex justify-end gap-2 border-t border-crema-200 pt-1"><span>Saldo actual: <b>$ ${T("4.999")}</b></span></div>
        <div class="flex gap-2"><span class="mock-campo">Pago ▾</span><span class="mock-campo">$ …</span><span class="mock-btn">Registrar</span></div>
      </div>`,
    pasos: [
      "El <b>cliente</b> viene puesto si hay uno solo; si no, elegilo. Se ve cada movimiento con el saldo acumulado fila por fila.",
      "Tocá el <b>número de pedido</b> (el #12 en azul) de una COMPRA y se abre su <b>remito</b>: cómo se formó ese pedido, con artículos, cantidades y los precios congelados de ese día. Se puede imprimir. En el estado de cuenta impreso el botón no aparece.",
      "Cada COMPRA muestra también el <b>turno</b>: con dos pedidos del mismo día, es lo único que distingue el de la mañana del de la tarde.",
      "Filtra el periodo con <b>Desde/Hasta</b> o los botones <b>Día / Semana / Mes / Todo</b>.",
      "Para cobrar: en <b>Registrar movimiento</b> elegí <b>Pago</b> y escribi el monto. Para <b>Devolución</b> o <b>Faltante</b> elegí los artículos abajo (como en un pedido) y el monto se calcula solo con la lista del cliente.",
      "La cuenta arranca <b>sin detalles</b>: una línea por movimiento, con número de pedido, importes y saldo. Tocá <b>Con detalles</b> y debajo de cada compra aparece <b>qué se pidió ese día</b>: cada artículo con su cantidad y su importe. El botón pasa a decir <b>Sin detalles</b>: tocalo para ocultarlos.",
      "Tocá <b>Imprimir</b> para imprimir o guardar como PDF: sale <b>igual que en pantalla</b>, con o sin detalles y con el período elegido. Primero elegís cómo la querés ver, y después imprimís.",
      "Para poder anular hay que <b>habilitarlo primero</b>: arriba, el botón <b>Habilitar anulaciones</b> se pone rojo y recién entonces aparece un <b>Anular</b> al lado de cada compra. Arranca apagado siempre —y se vuelve a apagar cada vez que entrás— para que un dedo mal apoyado revisando la cuenta en el celular no deshaga un pedido.",
      "El <b>Anular</b> deshace el pedido completo: sale de la producción del día y al local le llega un aviso al celular. Antes de confirmar, el cartel muestra cliente, entrega, turno y total, y podés dejar el <b>motivo</b>.",
      "<b>Anular no borra nada de la cuenta.</b> La compra queda donde estaba y debajo aparece una <b>ANULACIÓN</b> por el mismo importe, en negativo, en la misma columna. El saldo vuelve a lo que era y en el estado de cuenta se ve qué pasó: que hubo un pedido y que se deshizo. Es como funciona cualquier cuenta corriente — un asiento no se elimina, se compensa con otro.",
    ],
    tips: [
      "El modo de detalle <b>se conserva</b> al cambiar de cliente o de fechas. Al salir de Cuentas y volver a entrar, empieza otra vez sin detalles.",
      "Hay <b>dos saldos</b> y no son lo mismo. El <b>Saldo al cierre</b> (en la fila de totales) es cómo quedó la cuenta al final del <b>periodo que estás mirando</b>. La <b>Deuda total de la cuenta</b> (abajo, en grande) es lo que el cliente debe <b>hoy</b>, contando también lo de afuera del periodo. Si el filtro es <b>Todo</b>, los dos dan igual.",
      "El saldo <b>nunca se edita a mano</b>: siempre es compras − devoluciones − faltantes − pagos.",
      "Cada pedido confirmado genera su COMPRA solo; si lo anulas, queda compensada con su ANULACIÓN. Nada se borra nunca: el pedido anulado sigue en el historial con sus artículos y sus precios, y el movimiento sigue en la cuenta. Lo que se deshace es el efecto, no el registro.",
      "<b>Anular no es lo mismo que registrar una devolución.</b> Se <b>anula</b> cuando el pedido no tendría que haber existido: se cargo mal, con el cliente equivocado, o el local avisó que no lo quiere. Se registra una <b>devolución</b> cuando el pedido salió bien y volvió mercadería. La diferencia se nota en Producción y en Informes: lo anulado no se produjo ni se vendió nunca; lo devuelto sí.",
      "Un pedido anulado <b>no vuelve atrás</b>. Si fue un error, se carga de nuevo — y con los precios de hoy, que pueden no ser los de aquel día.",
      "El botón <b>solo aparece</b> si tu usuario tiene el permiso <b>Anular pedidos</b>: anular mueve la producción y los informes, no nada más la cuenta. Es el mismo botón que está en la pantalla <b>Pedidos</b>, que es donde conviene usarlo para el día a día.",
    ],
  },
  {
    permiso: "listas",
    hash: "#/listas",
    titulo: "Lista de precios",
    queEs:
      "Las listas de precios. Cada cliente tiene UNA asignada; hoy hay una sola, SHOPPINGS. Acá se ven, se imprimen con la marca, se editan, se renombran y se crean listas nuevas.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">Por cliente ▾</span><span class="mock-campo">o por lista ▾</span><span class="mock-btn-sec">+ Nueva lista</span><span class="mock-btn-sec">Editar</span><span class="mock-btn-sec">Imprimir</span></div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 flex items-center justify-between"><span class="font-bold">Lista de precios</span><span class="text-masa-400">SHOPPINGS — ${T("XXXXX")}</span></div>
          <table class="w-full"><tr class="text-masa-400"><td>Cod</td><td>Artículo</td><td>Emp</td><td>Precio</td></tr>
            <tr><td>2005</td><td>Olimpico blanco</td><td>x1</td><td class="font-bold">$ ${T("999")}</td></tr>
          </table>
        </div>
      </div>`,
    pasos: [
      "Elegí <b>por cliente</b> (muestra la lista que ese cliente tiene asignada) o <b>por lista</b> directamente.",
      "En modo <b>Ver</b>: la hoja sale limpia, agrupada por categoría, lista para imprimir con el logo.",
      "Tocá <b>Editar</b> para cambiar precios (quedan en ambar), quitar artículos (tachito = precio 0) o agregar artículos que faltan.",
      "Tocá <b>Guardar cambios</b>: se aplican todos juntos.",
      "<b>+ Nueva lista</b> crea una lista vacía desde cero y la abre en modo editar.",
      "<b>Renombrar</b> cambia el nombre o la descripción de la lista que estás viendo.",
    ],
    tips: [
      "OJO: editar una lista afecta a <b>todos los clientes</b> que la usan (la app te lo avisa con un cartel ambar).",
      "Los pedidos ya confirmados NO cambian: el precio quedó congelado al confirmarlos.",
      "Antes el desplegable arrastraba 30 listas vacías que venían de la instalación. Se borraron: quedó solo <b>SHOPPINGS</b>, la que usan los locales.",
    ],
  },
  {
    permiso: "subas",
    hash: "#/subas",
    titulo: "Subas",
    queEs:
      "El aumento de precios: aplicas un % a una o varias listas y los precios nuevos quedan redondeados a pesos enteros. Con vista previa obligatoria e historial de todas las subas.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">☑ SHOPPINGS</span><span class="mock-campo">% 5</span><span class="mock-btn">Vista previa</span></div>
        <div class="rounded-lg bg-amber-50 p-2 text-amber-800">Vista previa +5%: se modifican ${T("30")} precios. Los pedidos confirmados NO cambian.</div>
        <table class="w-full"><tr class="text-masa-400"><td>Artículo</td><td>Actual</td><td>Nuevo</td></tr>
          <tr><td>Olimpico blanco</td><td>$ ${T("78")}</td><td class="font-bold">$ ${T("82")}</td></tr>
        </table>
        <div class="flex gap-2"><span class="mock-btn">Aplicar suba +5%</span><span class="mock-btn-sec">Cancelar</span></div>
      </div>`,
    pasos: [
      "Tilda las <b>listas</b> a aumentar (o 'Todas').",
      "Escribi el <b>% de aumento</b> y, si queres, una nota (ej: 'suba de harina julio').",
      "Tocá <b>Vista previa</b>: se ve cada precio actual → nuevo, SIN cambiar nada todavia.",
      "Revisá y tocá <b>Aplicar suba</b>: los precios se actualizan y la suba queda en el <b>historial</b> (fecha, lista, %, nota).",
    ],
    tips: [
      "Los precios nuevos quedan <b>redondeados a pesos enteros</b> (sin decimales), como siempre se hizo.",
      "Nunca se aplica nada sin vista previa: si te arrepentis, Cancelar.",
    ],
  },
  {
    permiso: "informes",
    hash: "#/informes",
    titulo: "Informes",
    queEs:
      "El resumen de ventas: cuanto compro cada cliente, cuanto se vendio por día y cuales son los productos más vendidos, en el periodo que elijas.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-2"><span class="mock-campo">Desde 01/07</span><span class="mock-campo">Hasta 16/07</span><span class="mock-btn-sec">Día</span><span class="mock-btn-sec">Mes</span><span class="mock-btn-sec">Imprimir</span></div>
        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-lg border border-crema-200 p-2"><div class="font-bold text-masa-700">VENTAS POR CLIENTE</div>
            <div class="flex justify-between"><span>${T("XXXXXXX")}</span><span>$ ${T("9.999")}</span></div>
            <div class="flex justify-between border-t border-crema-100 font-bold"><span>TOTAL</span><span>$ ${T("99.999")}</span></div></div>
          <div class="rounded-lg border border-crema-200 p-2"><div class="font-bold text-masa-700">MÁS VENDIDOS</div>
            <div class="flex justify-between"><span>Empanada J y Q</span><span>${T("99")} bultos</span></div></div>
        </div>
      </div>`,
    pasos: [
      "Elegí el periodo con <b>Desde/Hasta</b> o los botones rápidos <b>Día / Semana / Mes / Todo</b>.",
      "Se muestran tres cuadros: <b>ventas por cliente</b> (con el total general), <b>ventas por día</b> y <b>productos más vendidos</b>.",
      "Tocá <b>Imprimir</b> si necesitás la hoja.",
    ],
    tips: ["Solo cuentan los pedidos CONFIRMADOS (los anulados no suman)."],
  },
  {
    permiso: "clientes",
    hash: "#/clientes",
    titulo: "Clientes",
    queEs:
      "El padron de locales y clientes: nombre, contacto, teléfono, dirección, que lista de precios tiene asignada cada uno y — desde la llavecita — el acceso web para que pidan solos desde el celular.",
    mock: `
      <div class="mock space-y-2">
        <div class="text-masa-400">⊕ NUEVO CLIENTE</div>
        <table class="w-full"><tr class="text-masa-400"><td>Código</td><td>Nombre</td><td>Lista</td><td>Teléfono</td><td></td></tr>
          <tr><td>LO0001</td><td>${T("XXXXXXXXXX")}</td><td>SHOPPINGS</td><td>${T("099999999")}</td><td>🔑 ✎</td></tr>
        </table>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 font-bold text-masa-700">ACCESO WEB</div>
          <div class="mock-campo w-full">Correo de Google del encargado: ${T("xxxxxxxx")}@gmail.com</div>
          <div class="mt-1 text-masa-400">Entra con «Entrar con Google». No hay clave.</div>
        </div>
      </div>`,
    pasos: [
      "Para dar de alta: abrí <b>⊕ Nuevo cliente</b>, completa código (LO… para los locales), nombre y la <b>lista de precios</b> que le corresponde.",
      "Para modificar: tocá el <b>lápiz</b> de la fila → podés cambiar todo, incluida la lista, y activar/desactivar el cliente.",
      "Para que el local pida solo: tocá la <b>llavecita</b>, escribí el <b>correo de Google</b> del encargado y tocá <b>Crear acceso</b>. No hay usuario ni clave que pasarle: entra a la misma dirección web y toca <b>Entrar con Google</b> con esa cuenta.",
      "Si cambia el encargado, entrá de nuevo a la llavecita, poné el correo de la persona nueva y tocá <b>Guardar correo</b>: la cuenta de antes deja de entrar <b>en el acto</b>.",
      "<b>Dar de baja</b> le corta el acceso al local sin tocar su historial de pedidos ni su cuenta.",
    ],
    tips: [
      "El <b>código no se puede repetir</b>: si ya existe, la app te avisa con un cartel.",
      "Cambiarle la lista a un cliente cambia los precios de sus <b>próximos</b> pedidos (los viejos no se tocan).",
      "Los clientes no se borran: se <b>desactivan</b> (conservan su historial y su cuenta).",
      "El usuario del local <b>solo ve lo suyo</b>: su catálogo, sus pedidos y su cuenta. No llega a ninguna pantalla de administración.",
      "Los locales <b>no tienen clave</b>, así que no hay clave que se pierda, se preste o haya que resetear. Sirve cualquier cuenta de Google, sea Gmail o el correo de una empresa.",
      "En Gmail los <b>puntos no importan</b>: juan.perez@gmail.com y juanperez@gmail.com son la misma casilla y entran igual. En cualquier otro correo hay que escribirlo exacto.",
    ],
  },
  {
    permiso: "productos",
    hash: "#/productos",
    titulo: "Productos",
    queEs:
      "El catálogo completo: código, descripción, categoría, área de producción, empaque (unidades por bulto) e IVA.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex gap-2"><span class="mock-campo">🔍 Buscar…</span><span class="text-masa-400">⊕ NUEVO PRODUCTO</span></div>
        <table class="w-full"><tr class="text-masa-400"><td>Código</td><td>Descripción</td><td>Categoría</td><td>Empaque</td><td>IVA</td><td></td></tr>
          <tr><td>2005</td><td>Olimpico blanco</td><td>Sandwichería</td><td>x1</td><td>22%</td><td>✎</td></tr>
          <tr><td>3004</td><td>Scones (bandeja 16 un.)</td><td>Panificados</td><td>x16</td><td>22%</td><td>✎</td></tr>
        </table>
        <div class="text-masa-400">Mostrando 1–15 de 30 · ‹ ›</div>
      </div>`,
    pasos: [
      "<b>Buscar</b> filtra por nombre o código al instante.",
      "Alta: <b>⊕ Nuevo producto</b> — al elegir la categoría, el <b>código se sugiere solo</b> (siguiente de la familia, ej. Sandwichería → 2xxx).",
      "Edición: el <b>lápiz</b> abre todo (descripción, categoría, área, empaque, IVA, activo).",
    ],
    tips: [
      "El <b>empaque</b> es clave: producción multiplica bultos × empaque para saber las unidades a producir. Los scones se piden por bandeja (empaque 16); casi todo lo demas va por unidad (empaque 1).",
      "El código duplicado se <b>bloquea</b> con un aviso.",
    ],
  },
  {
    permiso: "categorias",
    hash: "#/categorias",
    titulo: "Categorías",
    queEs:
      "Las familias de productos (Empanadas y salados, Sandwichería, Panificados, Confitería, Tortas y budines, Ensaladas): crear, renombrar, borrar, asignarles el prefijo de código y aplicar IVA masivo a todos sus productos.",
    mock: `
      <div class="mock space-y-2">
        <table class="w-full"><tr class="text-masa-400"><td>Nombre</td><td>Prefijo</td><td>Productos</td><td>IVA</td><td></td></tr>
          <tr><td>Sandwichería</td><td>2</td><td>8</td><td><span class="mock-campo">22% ▾</span> <span class="mock-btn-sec">Aplicar</span></td><td>✎ 🗑</td></tr>
          <tr><td>Confitería</td><td>4</td><td>7</td><td><span class="mock-campo">mixto ▾</span> <span class="mock-btn-sec">Aplicar</span></td><td>✎ 🗑</td></tr>
        </table>
      </div>`,
    pasos: [
      "El número de la columna <b>Productos</b> se toca: abre la lista de artículos de esa categoría y podés <b>moverlos</b> a otra.",
      "La columna <b>IVA</b> aplica una tasa (0/10/22%) a TODOS los productos de la categoría de una vez ('mixto' = tienen tasas distintas). Es la forma rápida de cargar el IVA la primera vez.",
      "El <b>prefijo</b> define la familia de códigos (Sandwichería=2 → 2001, 2002…): así el alta de productos sugiere el código solo.",
    ],
    tips: ["No se puede borrar una categoría con productos: primero movelos a otra."],
  },
  {
    permiso: "__admin",
    hash: "#/usuarios",
    titulo: "Usuarios",
    queEs:
      "Solo para el administrador: crear los usuarios del sistema y elegir con tildes que puede usar cada uno. El menú de cada usuario muestra unicamente lo permitido.",
    mock: `
      <div class="mock space-y-2">
        <div class="text-masa-400">⊕ NUEVO USUARIO</div>
        <div class="flex flex-wrap gap-1"><span class="mock-campo">☑ Pedido</span><span class="mock-campo">☑ Pedidos web</span><span class="mock-campo">☑ Producción</span><span class="mock-campo">☐ Cuentas</span><span class="mock-campo">☐ Subas</span></div>
        <table class="w-full"><tr class="text-masa-400"><td>Usuario</td><td>Tipo</td><td>Permisos</td><td>Estado</td><td></td></tr>
          <tr><td>${T("repartidor")}</td><td>usuario</td><td>Pedido, Producción</td><td>Activo</td><td>✎</td></tr>
        </table>
      </div>`,
    pasos: [
      "Elegí el <b>tipo</b>: <b>interno</b> (gente de la confitería) o <b>de local</b> (el encargado que pide desde el celular).",
      "Si es <b>interno</b>: usuario + <b>correo de Google</b> + tildar los <b>permisos</b> (o marcar 'Es administrador' para que vea todo).",
      "Si es <b>de local</b>: usuario + <b>correo de Google</b> + tildar <b>que locales maneja</b>. No lleva permisos: solo ve el portal de pedidos. Se pueden tildar <b>varios</b> locales, para una persona que pide por más de uno.",
      "El <b>lápiz</b> permite cambiar permisos o locales, <b>cambiarle el correo</b> (la cuenta de antes deja de entrar en el acto) y <b>desactivar</b> al usuario.",
      "Arriba dice <b>cuántos usuarios hay en uso sobre el máximo de 10</b> y cuántos lugares quedan. Con los 10 ocupados no deja crear otro ni reactivar uno dado de baja.",
    ],
    tips: [
      "El que NO es admin no puede ajustar precios en el pedido ni ver esta pantalla.",
      "No podés quitarte tus propios permisos de admin (para no quedarte afuera).",
      "El usuario con la etiqueta <b>PROTEGIDO</b> es la cuenta de <b>soporte</b>, la de quien mantiene el sistema: ningún otro administrador lo puede desactivar, sacarle el admin ni cambiarle el correo. <b>No ocupa ninguno de los 10 lugares</b>.",
      "El permiso <b>Pedidos web</b> es el que decide a quién le llega el aviso cuando un local manda un pedido.",
      "<b>Anular pedidos</b> conviene darlo con cuentagotas: deshace un pedido confirmado, le saca la compra de la cuenta al cliente y lo quita de la producción del día. <b>Pedidos (lista)</b>, en cambio, es solo mirar, y se puede dar tranquilo.",
      "Para un local con <b>un solo</b> encargado, el atajo es la <b>llavecita</b> de la pantalla de Clientes: ponés el correo y listo. Esta pantalla es para el caso de varios locales por persona.",
      "Un <b>mismo correo no puede estar en dos usuarios</b>: si esa persona tiene que pedir por otro local más, se le tilda acá en vez de crearle otro usuario.",
      "<b>Nadie tiene clave</b>: todos entran con Google. Por eso no hay clave que resetear, que se pierda ni que se preste.",
      "<b>Cómo se cuentan los 10:</b> son usuarios <b>activos</b>, sumando internos y locales. <b>No cuentan</b> los desactivados ni la cuenta de soporte. Así que los 10 lugares son enteros para la confitería.",
      "Para hacerle lugar a alguien nuevo alcanza con <b>desactivar</b> a otro: deja de entrar en el acto y libera su lugar. No se borra, queda en la lista en gris, y se puede reactivar cuando haya lugar.",
      "Si hacen falta <b>más de 10</b>, hay que pedirlo a soporte: el máximo no se cambia desde esta pantalla.",
      "Un usuario de local dado de baja no entra más, y a un local inactivo no se le puede asignar un usuario nuevo.",
    ],
  },
  {
    permiso: "__admin",
    hash: "#/auditoria",
    titulo: "Auditoría",
    queEs:
      "Solo para el administrador: el registro de cambios. Cada alta, edición, anulación, pago o suba queda anotada con fecha, hora, usuario y detalle.",
    mock: `
      <div class="mock space-y-2">
        <table class="w-full"><tr class="text-masa-400"><td>Fecha</td><td>Usuario</td><td>Acción</td><td>Detalle</td></tr>
          <tr><td>16/07 14:32</td><td>${T("repartidor")}</td><td><span class="mock-btn-sec">crear pedido</span></td><td>Pedido #12 ${T("XXXXXXX")} total $ ${T("9.999")}</td></tr>
          <tr><td>16/07 10:15</td><td>admin</td><td><span class="mock-btn-sec">suba precios</span></td><td>Suba +5% en Lista22 (${T("97")} precios)</td></tr>
        </table>
      </div>`,
    pasos: [
      "Filtra por fecha con <b>Desde/Hasta</b> o los botones <b>Día / Semana / Mes / Todo</b>.",
      "Cada fila dice <b>quién</b> hizo <b>qué</b> y <b>cuándo</b> (hora de Montevideo).",
    ],
    tips: ["Útil para revisar 'quién cambió este precio' o 'quién anuló aquel pedido'."],
  },
  {
    permiso: "__admin",
    hash: "#/empresa",
    titulo: "Empresa",
    queEs:
      "Solo para el administrador: los datos de la confitería. Es lo que sale impreso en el encabezado de cada remito y de cada estado de cuenta, así que lo que se escribe acá lo ve el cliente.",
    mock: `
      <div class="mock space-y-1">
        <div class="mock-campo w-full">Nombre: Confitería Piedra y Sol</div>
        <div class="mock-campo w-full">Dirección: Jose Nasazzi 1022</div>
        <div class="mock-campo w-full">Ciudad: Montevideo, Uruguay</div>
        <div class="mock-campo w-full">Teléfono: 2209 3900</div>
        <div class="mock-campo w-full">Email (opcional): —</div>
        <span class="mock-btn">Guardar</span>
      </div>`,
    pasos: [
      "Corregí el dato que haga falta y tocá <b>Guardar</b>.",
      "El próximo remito que imprimas ya sale con el dato nuevo. Los remitos viejos no se tocan.",
    ],
    tips: [
      "El logo del remito es la marca <b>rb roberto barcos</b> y está escrita en el sistema, no es una imagen: no se cambia desde acá.",
    ],
  },
  {
    permiso: null,
    hash: "#/avisos",
    titulo: "Avisos",
    queEs:
      "La campana de la barra de arriba. Junta las novedades del sistema: para administración, los pedidos que mandan los locales; para el local, cuando su pedido queda confirmado.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex items-center justify-between"><span class="font-bold">Avisos</span><span class="mock-btn-sec">Limpiar avisos</span></div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="font-bold">Pedido web nuevo</div>
          <div class="text-masa-500">${T("XXXXXXXXXX")} pidió para el 17/07/2026 de mañana.</div>
          <div class="text-masa-400">17/07, 08:12 a. m.</div>
        </div>
      </div>`,
    pasos: [
      "Cuando hay algo sin leer, la <b>campana</b> muestra un número. Tocala y entras a la lista.",
      "Al entrar quedan marcados como leídos y el número se apaga solo.",
      "Tocá un aviso y te lleva a dónde pasó la cosa (Pedidos web, o Mis pedidos si sos del local).",
      "<b>Limpiar avisos</b> borra la lista. Los pedidos no se tocan: los seguís viendo donde estaban.",
    ],
    tips: [
      "La campana funciona <b>siempre</b>, aunque no hayas activado las notificaciones del celular.",
      "Para que además suene el teléfono o salte el aviso en la computadora: arriba de esta misma lista está el botón de <b>notificaciones</b> (en el celular también está en el menú ☰). Se pone <b>verde</b> cuando llegan y <b>rojo</b> cuando no. Se activa <b>en cada dispositivo</b> por separado.",
      "Con las notificaciones activadas, la pantalla que estás mirando <b>se actualiza sola</b> al llegar el aviso.",
    ],
  },
  {
    permiso: null,
    hash: "#/clave",
    titulo: "Entrar al sistema",
    sinEnlace: true,
    queEs:
      "Se entra de una sola forma: con Google. No hay usuario ni clave, ni para administración ni para los locales. Entra quien tenga su correo cargado en un usuario activo.",
    mock: `
      <div class="mock space-y-2">
        <div class="mock-campo w-full text-center font-medium">G&nbsp; Entrar con Google</div>
        <div class="rounded-lg bg-red-50 p-2 text-red-700">Esa cuenta de Google no tiene acceso. Probá con otra cuenta…</div>
      </div>`,
    pasos: [
      "Tocá <b>Entrar con Google</b> y elegí tu cuenta. Volvés solo al sistema, ya adentro.",
      "Google muestra <b>siempre</b> el selector de cuentas, aunque tengas una sola: es para que nadie entre sin querer con la cuenta equivocada.",
      "La sesión queda abierta <b>30 días</b> en ese dispositivo. Para cerrarla antes, <b>Salir</b>.",
    ],
    tips: [
      "Si dice <b>«esa cuenta no tiene acceso»</b>, casi siempre se eligió otra de las cuentas del teléfono, o el correo quedó mal escrito. Se revisa en <b>Usuarios</b> (o en la llavecita de <b>Clientes</b> si es un local).",
      "En Gmail los <b>puntos no importan</b>: juan.perez@gmail.com y juanperez@gmail.com entran igual. En cualquier otro correo hay que escribirlo exacto.",
      "Hay <b>una sola sesión por usuario</b>: si entrás en otro dispositivo, en el primero se cierra y te avisa. Por eso cada persona tiene que tener el suyo.",
      "El sistema <b>le pide a Google solo el correo</b>, para saber quién sos. No ve mails, contactos ni archivos.",
      "Para sacarle el acceso a alguien alcanza con <b>desactivarlo</b> o cambiarle el correo: deja de entrar en el acto, aunque tuviera la sesión abierta.",
      "Como no hay claves, <b>no hay nada que se pueda adivinar probando</b>: la seguridad de la entrada es la de la cuenta de Google de cada uno. Conviene que tengan la verificación en dos pasos.",
    ],
  },
];

export async function renderAyuda(contenedor: HTMLElement): Promise<void> {
  let sesion: Sesion | null = null;
  try {
    sesion = await api.get<Sesion>("/api/sesion");
  } catch {
    /* sin sesion no deberia llegar aca */
  }
  const puede = (s: SeccionAyuda): boolean => {
    if (!s.permiso) return true;
    if (sesion?.es_admin) return true;
    if (s.permiso === "__admin") return false;
    return (sesion?.permisos ?? []).includes(s.permiso);
  };
  const visibles = SECCIONES.filter(puede);

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina flex items-center gap-2"><span class="text-masa-600">${iconoAyuda}</span>Centro de ayuda</h1>
      <p class="subtitulo">Cómo usar cada parte del sistema, paso a paso. Las pantallas son ilustrativas: los datos sensibles van tapados.</p>
      <p class="mt-2 text-sm text-masa-600">Para alternar <b>Modo oscuro / Modo claro</b>, abrí el menú de tu cuenta en la computadora o el menú <b>☰</b> en el celular. La elección se guarda en cada dispositivo; los papeles impresos conservan su aspecto claro.</p>
    </div>

    <div class="mb-4 flex flex-wrap gap-1">
      ${visibles
        .map(
          (s, i) => `<a href="#sec-${i}" class="btn-sec px-2 py-1 text-xs" data-idx="${i}">${s.titulo}</a>`,
        )
        .join("")}
    </div>

    <div class="mb-4 rounded-xl bg-crema-100 p-4 text-sm text-masa-700">
      <span class="mr-1 inline-block align-middle">${logoApp("h-5 w-5 inline-block align-middle")}</span>
      <b>Cómo funciona el día a día:</b> el local manda su pedido desde el celular
      y queda <b>pendiente</b>; administración también puede cargarlo a mano desde <b>Pedido</b>.
      Al <b>confirmar</b> —en Pedidos web o al tomarlo a mano— el precio queda congelado, el pedido suma a la
      <b>producción de mañana</b> y anota la <b>COMPRA</b> en la cuenta de ese local.
      A la madrugada la cuadra produce lo del panel de Producción; de día se reparte con el remito y se registran
      devoluciones, faltantes y pagos.
    </div>

    <div class="space-y-3">
      ${visibles
        .map(
          (s, i) => `
        <details id="sec-${i}" class="tarjeta p-0" ${visibles.length === 1 ? "open" : ""}>
          <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
            <span class="text-masa-500">${iconoRuta(s.hash)}</span>
            <span class="flex-1">${s.titulo}</span>
            ${s.permiso === "__admin" ? `<span class="rounded-full bg-masa-100 px-2 py-0.5 text-[10px] font-semibold normal-case text-masa-800">solo admin</span>` : ""}
          </summary>
          <div class="grid grid-cols-1 gap-4 px-4 pb-4 lg:grid-cols-2">
            <div>
              <p class="mb-3 text-sm text-masa-700">${s.queEs}</p>
              <h3 class="etiqueta">Paso a paso</h3>
              <ol class="mb-3 list-decimal space-y-1 pl-5 text-sm text-masa-800">
                ${s.pasos.map((p) => `<li>${p}</li>`).join("")}
              </ol>
              <h3 class="etiqueta">Bueno saber</h3>
              <ul class="list-disc space-y-1 pl-5 text-sm text-masa-600">
                ${s.tips.map((t) => `<li>${t}</li>`).join("")}
              </ul>
            </div>
            <div>
              <h3 class="etiqueta">Asi se ve la pantalla</h3>
              ${s.mock}
              ${s.sinEnlace ? "" : `<a href="${s.hash}" class="btn-sec mt-2 inline-flex text-xs">Ir a ${s.titulo} →</a>`}
            </div>
          </div>
        </details>`,
        )
        .join("")}
    </div>`;

  // Los chips del indice abren la seccion y bajan hasta ella.
  contenedor.querySelectorAll<HTMLAnchorElement>("[data-idx]").forEach((a) => {
    a.addEventListener("click", (ev) => {
      ev.preventDefault();
      const det = contenedor.querySelector<HTMLDetailsElement>(`#sec-${a.dataset.idx}`);
      if (det) {
        det.open = true;
        det.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });
}
